import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, Mail } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import SignInForm from "@/components/auth/sign-in-form";
import SignUpForm from "@/components/auth/sign-up-form";
import Loader from "@/components/shared/loader";
import { featureFlagsQueryOptions } from "@/lib/query-options";
import { orpc } from "@/utils/orpc";

const loginSearchSchema = z.object({
	token: z.string().optional(),
	redirect: z.string().optional(),
	expired: z.string().optional(),
});

export const Route = createFileRoute("/login")({
	validateSearch: loginSearchSchema,
	component: RouteComponent,
});

/**
 * Validates redirect path to prevent open redirect vulnerabilities.
 * Only allows same-origin paths starting with "/".
 */
function getSafeRedirectPath(redirect?: string): string | undefined {
	if (!redirect) {
		return undefined;
	}

	// Must start with "/" but not "//" (protocol-relative URL)
	if (!redirect.startsWith("/") || redirect.startsWith("//")) {
		return undefined;
	}

	// Parse relative to current origin to validate
	try {
		const url = new URL(redirect, window.location.origin);
		// Ensure it stays on the same origin (handles edge cases like /\evil.com)
		if (url.origin !== window.location.origin) {
			return undefined;
		}
		// Return just the pathname + search + hash to strip any injection
		return url.pathname + url.search + url.hash;
	} catch {
		return undefined;
	}
}

function RouteComponent() {
	const { token, redirect, expired } = Route.useSearch();
	const [showSignIn, setShowSignIn] = useState(!token); // Show sign-up if token present
	const redirectTo = getSafeRedirectPath(redirect);
	const showExpiredMessage = expired === "true";

	// Fetch feature flags to determine invite-only mode
	const { data: featureFlags } = useQuery(featureFlagsQueryOptions());

	// Default to invite-only mode if feature flags not loaded (safer default)
	const inviteOnlyMode = featureFlags?.inviteOnlyMode ?? true;

	// Validate invitation token if present
	const {
		data: invitation,
		isLoading: isValidating,
		error: validationError,
	} = useQuery({
		...orpc.validateInvitationToken.queryOptions({
			input: { token: token || "" },
		}),
		enabled: !!token,
	});

	// Loading state while validating token
	if (token && isValidating) {
		return (
			<div className="mx-auto mt-10 w-full max-w-md p-6 text-center">
				<Loader />
				<p className="mt-4 text-muted-foreground">Validating invitation...</p>
			</div>
		);
	}

	// Invalid token error state
	if (token && validationError) {
		return (
			<div className="mx-auto mt-10 w-full max-w-md p-6">
				<div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
					<AlertCircle className="mx-auto h-12 w-12 text-destructive" />
					<h1 className="mt-4 font-bold text-xl">Invalid Invitation</h1>
					<p className="mt-2 text-muted-foreground">
						{validationError.message ||
							"This invitation link is invalid or has expired."}
					</p>
					<p className="mt-4 text-muted-foreground text-sm">
						Please contact us to request a new invitation.
					</p>
				</div>
			</div>
		);
	}

	// Invite-only mode without token - show message
	if (inviteOnlyMode && !token && !showSignIn) {
		return (
			<div className="mx-auto mt-10 w-full max-w-md p-6">
				<div className="rounded-lg border bg-card p-6 text-center">
					<Mail className="mx-auto h-12 w-12 text-primary" />
					<h1 className="mt-4 font-bold text-2xl">Invitation Required</h1>
					<p className="mt-2 text-muted-foreground">
						Designs by Maryam uses an invitation-only registration system.
					</p>
					<p className="mt-4 text-muted-foreground text-sm">
						If you're a client, you'll receive an invitation email with a link
						to create your account.
					</p>
					<div className="mt-6">
						<button
							className="text-primary underline hover:text-primary/80"
							onClick={() => setShowSignIn(true)}
							type="button"
						>
							Already have an account? Sign In
						</button>
					</div>
				</div>
			</div>
		);
	}

	// Show sign-in or sign-up form
	return showSignIn ? (
		<SignInForm
			onSwitchToSignUp={() => setShowSignIn(false)}
			redirectTo={redirectTo}
			showExpiredMessage={showExpiredMessage}
		/>
	) : (
		<SignUpForm
			invitationToken={token}
			invitedEmail={invitation?.email}
			invitedRole={invitation?.role}
			onSwitchToSignIn={() => setShowSignIn(true)}
		/>
	);
}
